import { CaretDown, type Icon as PhosphorIcon } from '@phosphor-icons/react';
import { useId, type ReactNode } from 'react';
import { Button } from 'react-aria-components';
import { Icon } from '../atoms/Icon';
import { cn } from '../lib/cn';

export interface FormSectionProps {
  icon: PhosphorIcon;
  label: string;
  /** What is set, in a few words ("Mi, 28.10."); empty shows the placeholder. */
  summary?: ReactNode;
  placeholder?: string;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  children: ReactNode;
}

/**
 * One line of a form that opens in place (1.25, Mike 20.10.: "nicht 3–4 mal rein und zurück").
 * Closed it is a quiet summary, so a form reads as a few lines instead of a wall of buttons; the
 * form decides which line opens next.
 */
export function FormSection({
  icon,
  label,
  summary,
  placeholder,
  isOpen,
  onOpenChange,
  children,
}: FormSectionProps) {
  const id = useId();
  return (
    <div
      className={cn(
        'flex flex-col rounded-card px-inset-sm transition duration-(--shh-motion-duration-quick)',
        // open lines stand apart, so two open ones never merge into one block
        isOpen ? 'my-gap-xs bg-surface-sunken pb-inset-sm' : 'border-b border-border-subtle',
      )}
    >
      <Button
        aria-expanded={isOpen}
        aria-controls={isOpen ? id : undefined}
        onPress={() => onOpenChange(!isOpen)}
        className="flex min-h-touch-target w-full cursor-pointer items-center gap-gap-sm rounded-control text-left outline-none data-focus-visible:outline-3 data-focus-visible:outline-border-focus"
      >
        <Icon icon={icon} size="md" className="shrink-0 text-accent-secondary" />
        <span className="w-24 shrink-0 text-sm font-semibold text-text-secondary">{label}</span>
        <span
          className={cn(
            'min-w-0 flex-1 truncate',
            summary ? 'font-bold text-text-primary' : 'text-text-muted',
          )}
        >
          {summary || placeholder}
        </span>
        <Icon
          icon={CaretDown}
          size="sm"
          weight="bold"
          className={cn('shrink-0 text-text-muted transition', isOpen && 'rotate-180')}
        />
      </Button>
      {isOpen && (
        <div id={id} className="flex flex-col gap-gap-md pt-gap-xs">
          {children}
        </div>
      )}
    </div>
  );
}
